import React, { useState, useEffect } from 'react';
import styles from './VideoLinksDisplay.module.css';
import VideoEmbed from '/components/VideoEmbed/VideoEmbed';
import { detectPlatform } from '../../utils/detectPlatform';
import { safeExternalUrl } from '../../utils/safeExternalUrl';

/**
 * Normalize video links.
 * Ensures that each entry is an object with:
 * - a stable unique `id` (using the link string if no id is provided)
 * - the `link` value
 * - all five criteria fields (coreMove, variants, moveInContext, combinations, extendedTutorial)
 */
function normalizeVideoLinks(links) {
  return links.map((entry, idx) => {
    // Use entry.id if provided; otherwise, generate one from the link string.
    const id = entry.id ? entry.id : entry.link;
    if (typeof entry === 'string') {
      return {
        id,
        link: entry,
        coreMove: false,
        variants: false,
        moveInContext: false,
        combinations: false,
        extendedTutorial: false,
      };
    }
    return {
      id,
      link: entry.link,
      coreMove: typeof entry.coreMove === 'boolean' ? entry.coreMove : false,
      variants: typeof entry.variants === 'boolean' ? entry.variants : false,
      moveInContext: typeof entry.moveInContext === 'boolean' ? entry.moveInContext : false,
      combinations: typeof entry.combinations === 'boolean' ? entry.combinations : false,
      extendedTutorial:
        typeof entry.extendedTutorial === 'boolean' ? entry.extendedTutorial : false,
    };
  });
}

/**
 * Determine a group for a video link.
 * Priority order: coreMove > variants > moveInContext > combinations > extendedTutorial > ungrouped.
 */
const getGroup = (link) => {
  if (link.coreMove) return 'coreMove';
  if (link.variants) return 'variants';
  if (link.moveInContext) return 'moveInContext';
  if (link.combinations) return 'combinations';
  if (link.extendedTutorial) return 'extendedTutorial';
  return 'ungrouped';
};

// Define the order in which groups appear.
const groupOrder = {
  coreMove: 1,
  variants: 2,
  moveInContext: 3,
  combinations: 4,
  extendedTutorial: 5,
  ungrouped: 6,
};

/**
 * VideoLinksDisplay – A read-only component to display video links.
 * The component renders the video links in an accordion grouped by criteria.
 */
const VideoLinksDisplay = ({ content }) => {
  const contentId = content?.content_id;
  const contentVideoLinks = content?.video_links;
  const [videoLinks, setVideoLinks] = useState([]);
  // Expanded state is stored by the unique id of each video link.
  const [expandedViews, setExpandedViews] = useState({});

  useEffect(() => {
    if (!contentId && !contentVideoLinks) return;

    let links = [];
    if (contentVideoLinks) {
      if (typeof contentVideoLinks === 'string') {
        try {
          const parsed = JSON.parse(contentVideoLinks);
          if (Array.isArray(parsed)) {
            links = normalizeVideoLinks(parsed);
          } else {
            console.error('Parsed video_links is not an array:', parsed);
          }
        } catch (err) {
          console.error('Error parsing video_links:', err);
        }
      } else if (Array.isArray(contentVideoLinks)) {
        links = normalizeVideoLinks(contentVideoLinks);
      }
    }
    // Sort links by group order (using the same priority as the admin view).
    links.sort((a, b) => groupOrder[getGroup(a)] - groupOrder[getGroup(b)]);
    setVideoLinks(links);
    // Initialize expandedViews keyed by each link's id (all collapsed by default).
    const initialExpanded = {};
    links.forEach(link => {
      initialExpanded[link.id] = false;
    });
    setExpandedViews(initialExpanded);
  }, [contentId, contentVideoLinks]);

  const toggleView = (id) => {
    setExpandedViews((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getHeaderText = (linkStr) => {
    if (!linkStr) return 'No Link Provided';
    const platform = detectPlatform(linkStr);
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  // sortedLinks should already be sorted; we use it to determine group headers.
  const sortedLinks = [...videoLinks];

  return (
    <div className={styles.videoLinksDisplay}>
      {sortedLinks.length > 0 ? (
        sortedLinks.map((videoLink, index) => {
          const expanded = expandedViews[videoLink.id];
          const group = getGroup(videoLink);
          const externalHref = safeExternalUrl(videoLink.link);
          // Show a group header if this is the first item or if the group changes from the previous item.
          let showHeader = false;
          if (index === 0) {
            showHeader = true;
          } else {
            const prevGroup = getGroup(sortedLinks[index - 1]);
            if (prevGroup !== group) showHeader = true;
          }
          return (
            <React.Fragment key={videoLink.id}>
              {showHeader && (
                <h2>
                  {group === 'coreMove'
                    ? 'The Core Move'
                    : group === 'variants'
                    ? 'Varients'
                    : group === 'moveInContext'
                    ? 'The Move in Context'
                    : group === 'combinations'
                    ? 'Combinations'
                    : group === 'extendedTutorial'
                    ? 'Extended Tutorial'
                    : 'Ungrouped'}
                </h2>
              )}
              <div className={styles.videoLinkRow}>
                <div className={styles.accordionHeader}>
                  <h3>
                    <span>{getHeaderText(videoLink.link)}:</span>{' '}
                    {externalHref ? (
                      <a href={externalHref} target="_blank" rel="noopener noreferrer">
                        {videoLink.link}
                      </a>
                    ) : (
                      <span>No Link Provided</span>
                    )}
                  </h3>
                  <button
                    type="button"
                    className={styles.toggleIcon}
                    onClick={() => toggleView(videoLink.id)}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse video preview' : 'Expand video preview'}
                  >
                    {expanded ? '-' : '+'}
                  </button>
                </div>
                {expanded && (
                  <div className={styles.accordionContent}>
                    {videoLink.link && <VideoEmbed url={videoLink.link} />}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })
      ) : (
        <p>No video links available.</p>
      )}
    </div>
  );
};

export default VideoLinksDisplay;
